import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import type { TablesInsert, TablesUpdate } from '@/integrations/supabase/types';

type NodeInsert = TablesInsert<'nodes'>;
type NodeUpdate = TablesUpdate<'nodes'>;

interface NodeEdge {
  targetNodeId: string;
  relationshipType: 'prerequisite' | 'related' | 'deepens' | 'critiques' | 'applies' | 'exemplifies';
  weight?: number;
}

interface CreateNodeWithEdgesRequest {
  title: string;
  content: {
    text: string;
    preview?: string;
  };
  nodeType: 'article' | 'lesson' | 'concept' | 'reference';
  disciplineId: string;
  status?: 'draft' | 'in_review' | 'published' | 'archived';
  edges?: NodeEdge[];
  tags?: string[];
}

export const useCreateNode = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (node: Omit<NodeInsert, 'author_id'>) => {
      if (!user) throw new Error('User must be authenticated');

      const { data, error } = await supabase
        .from('nodes')
        .insert({
          ...node,
          author_id: user.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nodes'] });
    },
  });
};

export const useCreateNodeWithEdges = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: CreateNodeWithEdgesRequest) => {
      const { data, error } = await supabase.functions.invoke('create-node-with-edges', {
        body: request,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nodes'] });
    },
  });
};

export const useSubmitForReview = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ nodeId, submissionNote }: { nodeId: string; submissionNote?: string }) => {
      console.log('Starting submit for review process:', { nodeId, submissionNote });
      
      // First, verify the node exists and is in the correct state
      const { data: nodeCheck, error: nodeCheckError } = await supabase
        .from('nodes')
        .select('id, status, author_id, title')
        .eq('id', nodeId)
        .single();

      if (nodeCheckError) {
        console.error('Error checking node before submission:', nodeCheckError);
        throw new Error(`Node not found: ${nodeCheckError.message}`);
      }

      if (!nodeCheck) {
        throw new Error('Node not found');
      }

      if (nodeCheck.status !== 'draft') {
        throw new Error(`Cannot submit for review: content is currently ${nodeCheck.status}. Only draft content can be submitted.`);
      }

      console.log('Node validation passed:', nodeCheck);

      // Use direct database update as primary method (more reliable)
      console.log('Using direct database update for submit for review');
      
      // Simple update without triggering history constraints
      const { data: updatedNode, error: updateError } = await supabase
        .from('nodes')
        .update({ 
          status: 'in_review'
        })
        .eq('id', nodeId)
        .eq('status', 'draft') // Double-check status hasn't changed
        .select()
        .single();

      if (updateError) {
        console.error('Direct update failed:', updateError);
        throw new Error(`Failed to submit for review: ${updateError.message}`);
      }

      if (!updatedNode) {
        throw new Error('Node was not updated - it may have been modified by another user');
      }

      console.log('Submit for review successful:', updatedNode);
      return {
        success: true,
        node: updatedNode,
        message: 'Content submitted for review successfully'
      };
    },
    onSuccess: (data) => {
      console.log('Submit for review mutation successful, invalidating queries');
      queryClient.invalidateQueries({ queryKey: ['nodes'] });
      if (data.node) {
        queryClient.invalidateQueries({ queryKey: ['node', data.node.id] });
      }
    },
    onError: (error) => {
      console.error('Submit for review mutation failed:', error);
    },
  });
};

export const useUpdateNode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: NodeUpdate }) => {
      console.log('Updating node:', { id, updates });
      
      const { data, error } = await supabase
        .from('nodes')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Update error:', error);
        throw error;
      }
      
      console.log('Update successful:', data);
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['nodes'] });
      queryClient.invalidateQueries({ queryKey: ['node', data.id] });
    },
    onError: (error) => {
      console.error('Update node mutation failed:', error);
    },
  });
};

export const useDeleteNode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('nodes')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nodes'] });
    },
  });
};

export const useApproveContent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (nodeId: string) => {
      console.log('Approving content:', nodeId);
      
      const { data, error } = await supabase
        .from('nodes')
        .update({ 
          status: 'published',
          published_at: new Date().toISOString()
        })
        .eq('id', nodeId)
        .eq('status', 'in_review') // Only approve content that's in review
        .select()
        .single();

      if (error) {
        console.error('Approve content error:', error);
        throw error;
      }

      if (!data) {
        throw new Error('Content not found or not in review status');
      }

      console.log('Content approved:', data);
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['nodes'] });
      queryClient.invalidateQueries({ queryKey: ['node', data.id] });
    },
    onError: (error) => {
      console.error('Approve content mutation failed:', error);
    },
  });
};

export const useRejectContent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ nodeId, feedback }: { nodeId: string; feedback?: string }) => {
      console.log('Rejecting content:', { nodeId, feedback });
      
      const { data, error } = await supabase
        .from('nodes')
        .update({ 
          status: 'draft'
        })
        .eq('id', nodeId)
        .eq('status', 'in_review') // Only reject content that's in review
        .select()
        .single();

      if (error) {
        console.error('Reject content error:', error);
        throw error;
      }

      if (!data) {
        throw new Error('Content not found or not in review status');
      }

      console.log('Content rejected and returned to draft:', data);
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['nodes'] });
      queryClient.invalidateQueries({ queryKey: ['node', data.id] });
    },
    onError: (error) => {
      console.error('Reject content mutation failed:', error);
    },
  });
};
